/// <amd-module name="Feed/Legacy/Protocols/Maven/MavenDevelopment" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";

import { MinimalPackageVersion, Package, UpstreamSource } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { PackageUpstreamSource } from "Feed/Legacy/Components/PackageUpstreamSource";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isSafeProtocol } from "Feed/Legacy/Helpers/UrlHelper";
import { PackageAttribute } from "Feed/Legacy/Protocols/Components/PackageAttribute";
import { IProjectAttribute } from "Feed/Legacy/Protocols/Components/PackageDevelopment";
import { MavenCiConstants } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { htmlEncode } from "Feed/Legacy/Utils/Html";

export interface IMavenDevelopmentProps {
    pageContext: IVssPageContext;
    buildAttribute?: IProjectAttribute;
    codeAttribute?: IProjectAttribute;
    projectAttributes?: IProjectAttribute[];
    packageSummary: Package;
    packageVersion: MinimalPackageVersion;
    upstreamSources?: UpstreamSource[];
}

export class MavenDevelopment extends React.PureComponent<IMavenDevelopmentProps> {
    public render(): JSX.Element {
        if (!this.showDevelopment()) {
            return null;
        }

        const { buildAttribute, codeAttribute, projectAttributes } = this.props;

        return (
            <div className="maven-development">
                <PackageAttribute title={PackageResources.MavenAttributes_DevelopmentTitle}>
                    {this.props.upstreamSources && (
                        <div>
                            <div className="package-group-header" role="heading" aria-level={3}>
                                {PackageResources.PackageSource}
                            </div>
                            <div className="package-attribute-item">
                                <span className="bowtie-icon bowtie-packages package-attribute-item-icon" />
                                <PackageUpstreamSource
                                    pageContext={this.props.pageContext}
                                    packageSummary={this.props.packageSummary}
                                    packageVersion={this.props.packageVersion}
                                    upstreamSources={this.props.upstreamSources}
                                />
                            </div>
                        </div>
                    )}
                    {buildAttribute && buildAttribute.text && (
                        <div>
                            <div className="package-group-header" role="heading" aria-level={3}>
                                {PackageResources.MavenDevelopment_BuildHeading}
                            </div>
                            <div className="package-attribute-item" key={"build"}>
                                <span className={"package-attribute-item-icon " + buildAttribute.iconClass} />
                                {isSafeProtocol(buildAttribute.url || "") ? (
                                    <ExternalLink
                                        pageContext={this.props.pageContext}
                                        href={htmlEncode(buildAttribute.url)}
                                        ciContext={MavenCiConstants.MavenDevelopment}
                                        onClick={() =>
                                            CustomerIntelligenceHelper.publishEvent(
                                                this.props.pageContext,
                                                buildAttribute.onClickCiFeature
                                            )
                                        }
                                    >
                                        {buildAttribute.text}
                                        <span className="bowtie-icon bowtie-navigate-external" />
                                    </ExternalLink>
                                ) : (
                                    <span className="project-attibute-text">{buildAttribute.text}</span>
                                )}
                            </div>
                        </div>
                    )}
                    {codeAttribute && codeAttribute.text && (
                        <div>
                            <div className="package-group-spacer" />
                            <div className="package-group-header" role="heading" aria-level={3}>
                                {PackageResources.MavenDevelopment_CodeHeading}
                            </div>
                            <div className="package-attribute-item" key={"code"}>
                                <span className={"package-attribute-item-icon " + codeAttribute.iconClass} />
                                {isSafeProtocol(codeAttribute.url || "") ? (
                                    <ExternalLink
                                        pageContext={this.props.pageContext}
                                        href={htmlEncode(codeAttribute.url)}
                                        ciContext={MavenCiConstants.MavenDevelopment}
                                        onClick={() =>
                                            CustomerIntelligenceHelper.publishEvent(
                                                this.props.pageContext,
                                                codeAttribute.onClickCiFeature
                                            )
                                        }
                                    >
                                        {codeAttribute.text}
                                        <span className="bowtie-icon bowtie-navigate-external" />
                                    </ExternalLink>
                                ) : (
                                    <span className="project-attibute-text">{codeAttribute.text}</span>
                                )}
                            </div>
                        </div>
                    )}
                    {projectAttributes && projectAttributes.length > 0 && (
                        <div>
                            <div className="package-group-spacer" />
                            <div className="package-group-header" role="heading" aria-level={3}>
                                {PackageResources.MavenDevelopment_ProjectHeading}
                            </div>
                            {projectAttributes.map((projectAttribute: IProjectAttribute, index: number) => {
                                return (
                                    <div className="package-attribute-item" key={index}>
                                        <span className={"package-attribute-item-icon " + projectAttribute.iconClass} />
                                        {isSafeProtocol(projectAttribute.url || "") ? (
                                            <ExternalLink
                                                pageContext={this.props.pageContext}
                                                href={htmlEncode(projectAttribute.url)}
                                                ciContext={MavenCiConstants.MavenDevelopment}
                                                onClick={() =>
                                                    CustomerIntelligenceHelper.publishEvent(
                                                        this.props.pageContext,
                                                        projectAttribute.onClickCiFeature
                                                    )
                                                }
                                            >
                                                {projectAttribute.text}
                                                <span className="bowtie-icon bowtie-navigate-external" />
                                            </ExternalLink>
                                        ) : (
                                            <span className="project-attibute-text">{projectAttribute.text}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </PackageAttribute>
            </div>
        );
    }

    private showDevelopment(): boolean {
        return (
            this.props.upstreamSources != null ||
            (this.props.buildAttribute != null && this.props.buildAttribute.text != null) ||
            (this.props.codeAttribute != null && this.props.codeAttribute.text != null) ||
            (this.props.projectAttributes != null && this.props.projectAttributes.length > 0)
        );
    }
}
